export enum BinderType {
  HOME = "home",
  RESIDENT = "resident",
  CAREGIVER_JOB_SEARCH = "caregiver_job_search",
  STAFF = "staff",
}

export enum BinderFolderType {
  SYSTEM = "system",
  CUSTOM = "custom",
}

export class Binder {
  binderId: string | undefined;
  binderType: BinderType | undefined;
  facilityType?: string | undefined;
  professionalRole?: string | undefined;
  referenceType: string | undefined;
  referenceId: string | undefined;
  name: string | undefined;
  status: string | undefined;
  latestComplianceRunId?: string | undefined;
  createdAt: string | undefined;
  createdBy: string | undefined;
}

export class BinderFolder {
  folderId: string | undefined;
  binderId: string | undefined;
  folderCode: string | undefined;
  folderName: string | undefined;
  folderType: BinderFolderType | undefined;
  shareable?: boolean | undefined;
  sortOrder?: number | undefined;
  requiredForCompliance?: boolean | undefined;
  ruleRefs?: string[] | undefined;
  guidanceTitle?: string | undefined;
  guidanceDescription?: string | undefined;
  guidanceCitation?: string | undefined;
  guidanceSourceUrl?: string | undefined;
  suggestedDocuments?: string[] | undefined;
  documentTypeOptions?: string[] | undefined;
  createdAt: string | undefined;
  createdBy: string | undefined;
}

export class BinderDocument {
  binderDocumentId: string | undefined;
  binderId: string | undefined;
  folderId: string | undefined;
  title: string | undefined;
  isPrimaryDocument?: boolean | undefined;
  documentCategory?: string | undefined;
  documentNumber?: string | undefined;
  dateOfBirth?: string | undefined;
  status?: string | undefined;
  latestVersionId?: string | undefined;
  latestVersionNumber?: number | undefined;
  latestAnalysisId?: string | undefined;
  latestAnalysisStatus?: string | undefined;
  latestAnalyzedAt?: string | undefined;
  latestAnalyzedVersionId?: string | undefined;
  effectiveDate?: string | undefined;
  expirationDate?: string | undefined;
  reviewStatus?: string | undefined;
  reviewNote?: string | undefined;
  reviewUpdatedAt?: string | undefined;
  reviewUpdatedBy?: string | undefined;
  createdAt: string | undefined;
  createdBy: string | undefined;
}

export class BinderDocumentVersion {
  versionId: string | undefined;
  binderDocumentId: string | undefined;
  binderId: string | undefined;
  versionNumber: number | undefined;
  fileName: string | undefined;
  s3Key: string | undefined;
  mimeType?: string | undefined;
  notes?: string | undefined;
  uploadedAt: string | undefined;
  uploadedBy: string | undefined;
}

export class BinderDocumentAnalysis {
  analysisId: string | undefined;
  binderDocumentId: string | undefined;
  binderId: string | undefined;
  versionId: string | undefined;
  folderId?: string | undefined;
  folderCode?: string | undefined;
  folderName?: string | undefined;
  fileName?: string | undefined;
  mimeType?: string | undefined;
  status?: string | undefined;
  textractJobId?: string | undefined;
  failureReason?: string | undefined;
  extractionMethod?: string | undefined;
  extractedTextLength?: number | undefined;
  confidenceScore?: number | undefined;
  exactRuleMatchConfidence?: number | undefined;
  likelyDocumentType?: string | undefined;
  likelySatisfiesFolder?: string | undefined;
  ruleFitRationale?: string | undefined;
  signatureStatus?: string | undefined;
  detectedResidentName?: string | undefined;
  detectedProviderName?: string | undefined;
  detectedDocumentDate?: string | undefined;
  detectedEffectiveDate?: string | undefined;
  detectedExpirationDate?: string | undefined;
  summary?: string | undefined;
  recommendedAction?: string | undefined;
  warnings?: string[] | undefined;
  missingEvidence?: string[] | undefined;
  extractedFacts?: string[] | undefined;
  matchingSignals?: string[] | undefined;
  reviewReasons?: string[] | undefined;
  rawModelResponse?: string | undefined;
  createdAt?: string | undefined;
  createdBy?: string | undefined;
  startedAt?: string | undefined;
  completedAt?: string | undefined;
}

export class BinderComplianceRun {
  complianceRunId: string | undefined;
  binderId: string | undefined;
  binderType: BinderType | undefined;
  status: string | undefined;
  ruleSetVersion?: string | undefined;
  summary?: string | undefined;
  totalRuleCount?: number | undefined;
  compliantCount?: number | undefined;
  missingCount?: number | undefined;
  outdatedCount?: number | undefined;
  reviewNeededCount?: number | undefined;
  findings?: BinderComplianceFinding[] | undefined;
  createdAt: string | undefined;
  createdBy: string | undefined;
}

export class BinderRule {
  ruleId: string | undefined;
  binderType: BinderType | undefined;
  facilityType?: string | undefined;
  folderCode: string | undefined;
  title: string | undefined;
  description?: string | undefined;
  citation?: string | undefined;
  sourceUrl?: string | undefined;
  status?: string | undefined;
  effectiveFrom?: string | undefined;
  effectiveTo?: string | undefined;
  requiredForCompliance?: boolean | undefined;
  expectedDocumentCount?: number | undefined;
  versionNumber?: number | undefined;
  supersedesRuleId?: string | undefined;
  supersededByRuleId?: string | undefined;
  createdAt?: string | undefined;
  createdBy?: string | undefined;
  modifiedAt?: string | undefined;
  modifiedBy?: string | undefined;
}

export class BinderRuleSource {
  sourceId: string | undefined;
  binderType: BinderType | undefined;
  title: string | undefined;
  citation?: string | undefined;
  sourceType?: string | undefined;
  sourceUrl: string | undefined;
  status?: string | undefined;
  lastCheckedAt?: string | undefined;
  lastSeenContentHash?: string | undefined;
  lastSeenEffectiveDate?: string | undefined;
  lastSeenTitle?: string | undefined;
  lastAlertId?: string | undefined;
  lastSnapshotId?: string | undefined;
  snapshotCount?: number | undefined;
  createdAt?: string | undefined;
  createdBy?: string | undefined;
  modifiedAt?: string | undefined;
  modifiedBy?: string | undefined;
}

export class BinderRuleSnapshot {
  snapshotId: string | undefined;
  sourceId: string | undefined;
  binderType: BinderType | undefined;
  title?: string | undefined;
  citation?: string | undefined;
  sourceUrl?: string | undefined;
  detectedTitle?: string | undefined;
  effectiveDate?: string | undefined;
  contentHash?: string | undefined;
  htmlLength?: number | undefined;
  normalizedTextLength?: number | undefined;
  normalizedTextExcerpt?: string | undefined;
  rawHtmlS3Key?: string | undefined;
  normalizedTextS3Key?: string | undefined;
  previousSnapshotId?: string | undefined;
  changeDetected?: boolean | undefined;
  aiSummary?: string | undefined;
  aiSummaryStatus?: string | undefined;
  normalizedText?: string | undefined;
  rawHtml?: string | undefined;
  createdAt?: string | undefined;
  createdBy?: string | undefined;
}

export class BinderRuleChangeAlert {
  alertId: string | undefined;
  sourceId: string | undefined;
  binderType: BinderType | undefined;
  title?: string | undefined;
  citation?: string | undefined;
  sourceUrl?: string | undefined;
  detectionType?: string | undefined;
  reviewStatus?: string | undefined;
  previousContentHash?: string | undefined;
  newContentHash?: string | undefined;
  previousEffectiveDate?: string | undefined;
  newEffectiveDate?: string | undefined;
  previousTitle?: string | undefined;
  newTitle?: string | undefined;
  changeSummary?: string | undefined;
  summarySource?: string | undefined;
  previousSnapshotId?: string | undefined;
  newSnapshotId?: string | undefined;
  createdAt?: string | undefined;
  createdBy?: string | undefined;
  resolvedAt?: string | undefined;
  resolvedBy?: string | undefined;
}

export class BinderComplianceFinding {
  ruleId?: string | undefined;
  folderId?: string | undefined;
  folderCode?: string | undefined;
  folderName?: string | undefined;
  requirement?: string | undefined;
  status?: string | undefined;
  summary?: string | undefined;
  details?: string | undefined;
  documentCount?: number | undefined;
  latestDocumentTitle?: string | undefined;
  latestDocumentId?: string | undefined;
  latestVersionNumber?: number | undefined;
  lastUpdatedAt?: string | undefined;
  matchedDocumentIds?: string[] | undefined;
  aiAnalysisStatus?: string | undefined;
  aiLikelySatisfiesFolder?: string | undefined;
  aiConfidenceScore?: number | undefined;
  aiExactRuleMatchConfidence?: number | undefined;
  aiSignatureStatus?: string | undefined;
  aiDetectedResidentName?: string | undefined;
  aiDetectedProviderName?: string | undefined;
  aiDetectedDocumentDate?: string | undefined;
  aiDetectedEffectiveDate?: string | undefined;
  aiDetectedExpirationDate?: string | undefined;
  aiSummary?: string | undefined;
  aiRecommendedAction?: string | undefined;
  aiMatchingSignals?: string[] | undefined;
  aiReviewReasons?: string[] | undefined;
}

export class BinderShare {
  shareId?: string | undefined;
  binderId?: string | undefined;
  facilityType?: string | undefined;
  folderIds?: string[] | undefined;
  expirationOption?: "1" | "7" | "14" | "30" | "forever" | string | undefined;
  expirationDate?: string | undefined;
  folderNotes?: BinderFolderNote[] | undefined;
  binderType?: BinderType | undefined;
  shareMethod?: string | undefined;
  recipientUserId?: string | undefined;
  recipientEmail?: string | undefined;
  recipientName?: string | undefined;
  message?: string | undefined;
  shareStatus?: string | undefined;
  reviewStatus?: string | undefined;
  reviewNotes?: string | undefined;
  jobQuestions?: string | undefined;
  jobQuestionsSentAt?: string | undefined;
  jobQuestionAnswers?: string | undefined;
  jobQuestionsAnsweredAt?: string | undefined;
  jobQuestionsStatus?: string | undefined;
  reviewedAt?: string | undefined;
  reviewedByUserId?: string | undefined;
  reviewedByEmail?: string | undefined;
  ownerUserId?: string | undefined;
  ownerEmail?: string | undefined;
  ownerDisplayName?: string | undefined;
  createdAt?: string | undefined;
  createdBy?: string | undefined;
  modifiedAt?: string | undefined;
  modifiedBy?: string | undefined;
  history?: BinderShareHistoryItem[] | undefined;
}

export class BinderFolderNote {
  noteId?: string | undefined;
  folderId?: string | undefined;
  note?: string | undefined;
  createdAt?: string | undefined;
  createdByEmail?: string | undefined;
  createdByName?: string | undefined;
}

export class BinderShareHistoryItem {
  eventType?: string | undefined;
  title?: string | undefined;
  description?: string | undefined;
  createdAt?: string | undefined;
  actorLabel?: string | undefined;
}

export const DEFAULT_BINDER_RULE_SOURCES = {
  [BinderType.HOME]: [
    {
      title: "Personnel records rule",
      citation: "WAC 388-76-10198",
      sourceType: "WAC",
      sourceUrl: "https://app.leg.wa.gov/WAC/default.aspx?cite=388-76-10198",
    },
    {
      title: "AFH records guidance",
      citation: "DSHS AFH Records Guidance",
      sourceType: "DSHS_GUIDANCE",
      sourceUrl: "https://www.dshs.wa.gov/node/30670",
    },
  ],
  [BinderType.RESIDENT]: [
    {
      title: "Resident record content rule",
      citation: "WAC 388-76-10320",
      sourceType: "WAC",
      sourceUrl: "https://app.leg.wa.gov/WAC/default.aspx?cite=388-76-10320",
    },
    {
      title: "Resident record required rule",
      citation: "WAC 388-76-10315",
      sourceType: "WAC",
      sourceUrl: "https://app.leg.wa.gov/WAC/default.aspx?cite=388-76-10315",
    },
    {
      title: "AFH records guidance",
      citation: "DSHS AFH Records Guidance",
      sourceType: "DSHS_GUIDANCE",
      sourceUrl: "https://www.dshs.wa.gov/node/30670",
    },
  ],
  [BinderType.CAREGIVER_JOB_SEARCH]: [
    {
      title: "Personnel records rule",
      citation: "WAC 388-76-10198",
      sourceType: "WAC",
      sourceUrl: "https://app.leg.wa.gov/WAC/default.aspx?cite=388-76-10198",
    },
    {
      title: "Long-term care worker training requirements",
      citation: "RCW 74.39A.074",
      sourceType: "RCW",
      sourceUrl: "https://app.leg.wa.gov/RCW/default.aspx?cite=74.39A.074",
    },
    {
      title: "AFH training requirements overview",
      citation: "DSHS AFH Training Requirements",
      sourceType: "DSHS_GUIDANCE",
      sourceUrl: "https://www.dshs.wa.gov/node/2501",
    },
  ],
};

export const HOME_BINDER_SYSTEM_FOLDERS = [
  { code: "administration_policies", name: "Administration / Policies", requiredForCompliance: true },
  { code: "emergency_disaster", name: "Emergency / Disaster", requiredForCompliance: true },
  { code: "personnel_training", name: "Personnel / Training", requiredForCompliance: true },
  { code: "licensing_surveys", name: "Licensing / Surveys", requiredForCompliance: true },
  { code: "insurance_legal", name: "Insurance / Legal", requiredForCompliance: true },
  { code: "operations_logs", name: "Operations / Logs", requiredForCompliance: false },
];

export const RESIDENT_BINDER_SYSTEM_FOLDERS = [
  { code: "resident_information", name: "Resident Information Sheet", requiredForCompliance: true },
  { code: "assessment_preliminary_service_plan", name: "Assessment / Preliminary Service Plan", requiredForCompliance: true },
  { code: "negotiated_care_plan", name: "Negotiated Care Plan", requiredForCompliance: true },
  { code: "medical_professional_orders", name: "Medical Professional Orders", requiredForCompliance: true },
  { code: "legal_documents", name: "Legal Documents", requiredForCompliance: true },
  { code: "financial_record_keeping", name: "Financial Record Keeping", requiredForCompliance: true },
  { code: "personal_belongings_inventory", name: "Personal Belongings Inventory Log", requiredForCompliance: true },
  { code: "nurse_delegation", name: "Nurse Delegation Documents", requiredForCompliance: true },
  { code: "medication_logs", name: "Medication Logs / MARs", requiredForCompliance: true },
];

export const CAREGIVER_JOB_BINDER_SUPPORT_GROUPS = [
  {
    code: "id_work_eligibility",
    name: "ID and Work Eligibility",
    requiredForCompliance: true,
    helperText: "Upload the basic identity and work documents a provider will usually ask for first.",
    documentTypes: [
      "Driver license or photo ID",
      "State ID card",
      "Social Security card",
      "Work authorization document",
      "Contact information sheet",
    ],
  },
  {
    code: "background_checks",
    name: "Background Checks",
    requiredForCompliance: true,
    helperText: "Keep your Washington background check and fingerprint records together for employer review.",
    documentTypes: [
      "Washington name and date of birth background check",
      "Federal fingerprint background check",
      "BCCU result letter",
      "Disclosure statement",
    ],
  },
  {
    code: "basic_training_hca",
    name: "Basic Training and HCA Certification",
    requiredForCompliance: true,
    helperText: "Upload your basic long-term care worker training, HCA application, exam, and certification records.",
    documentTypes: [
      "70-hour long-term care worker basic training certificate",
      "75-hour HCA training certificate",
      "Core basic training certificate",
      "HCA application confirmation",
      "HCA exam pass result",
      "HCA certificate",
    ],
  },
  {
    code: "cpr_first_aid",
    name: "CPR and First Aid",
    requiredForCompliance: true,
    helperText: "Keep a current CPR and first-aid card or certificate available before providing care.",
    documentTypes: ["CPR card or certificate", "First-aid card or certificate", "CPR and First Aid card"],
  },
  {
    code: "ltcw_orientation_safety",
    name: "LTCW Orientation and Safety",
    requiredForCompliance: true,
    helperText: "Upload the required long-term care worker orientation and safety training records.",
    documentTypes: ["2-hour LTCW orientation certificate", "3-hour safety training certificate", "Orientation and safety completion record"],
  },
  {
    code: "facility_orientation_job_skills",
    name: "Facility Orientation and Job Skills",
    requiredForCompliance: true,
    helperText: "Keep facility orientation, role-specific training, and on-the-job skills competency records here.",
    documentTypes: ["Facility orientation checklist", "On-the-job skills checklist", "Job-specific competency record", "Resident-specific training record"],
  },
  {
    code: "annual_continuing_education",
    name: "Annual Continuing Education",
    requiredForCompliance: true,
    helperText: "Add each year's required continuing education proof and completion details.",
    documentTypes: ["Continuing education certificate", "Continuing education year summary", "Continuing education transcript", "Missing hours explanation"],
  },
  {
    code: "professional_credentials",
    name: "Professional Credentials",
    requiredForCompliance: false,
    helperText: "Add alternate or additional credentials such as HCA, CNA, or NAR records when they apply to you.",
    documentTypes: ["HCA certificate", "CNA certificate", "NAR credential proof", "Professional license or credential"],
  },
  {
    code: "specialty_dementia",
    name: "Dementia Specialty Training",
    requiredForCompliance: false,
    helperText: "Use this when a job or resident requires dementia specialty training.",
    documentTypes: ["Dementia specialty training certificate", "Dementia competency test result"],
  },
  {
    code: "specialty_mental_health",
    name: "Mental Health Specialty Training",
    requiredForCompliance: false,
    helperText: "Use this when a job or resident requires mental health specialty training.",
    documentTypes: ["Mental health specialty training certificate", "Mental health competency test result"],
  },
  {
    code: "specialty_developmental_disabilities",
    name: "Developmental Disabilities Specialty Training",
    requiredForCompliance: false,
    helperText: "Use this when a job or resident requires developmental disabilities specialty training.",
    documentTypes: ["Developmental disabilities specialty training certificate", "Developmental disabilities competency test result"],
  },
  {
    code: "nurse_delegation_core",
    name: "Nurse Delegation Core",
    requiredForCompliance: false,
    helperText: "Use this only when you are eligible for and assigned delegated nursing tasks.",
    documentTypes: ["Nurse delegation core certificate", "Nurse delegation competency record"],
  },
  {
    code: "nurse_delegation_diabetes",
    name: "Nurse Delegation Diabetes",
    requiredForCompliance: false,
    helperText: "Use this only when diabetes-related delegated tasks apply to your work.",
    documentTypes: ["Nurse delegation diabetes certificate", "Diabetes delegation competency record"],
  },
  {
    code: "food_worker",
    name: "Food Worker Card",
    requiredForCompliance: false,
    helperText: "Add a food worker card when the role or facility requires food handling.",
    documentTypes: ["Food Worker Card", "Food safety training record"],
  },
  {
    code: "tb_testing",
    name: "TB Testing",
    requiredForCompliance: false,
    helperText: "Keep employer-requested TB screening and related health records together.",
    documentTypes: [
      "Two-step TB skin test record",
      "TB blood test result",
      "Chest X-ray result",
      "TB symptom monitoring note",
      "New hire TB screening result",
    ],
  },
  {
    code: "other",
    name: "Other",
    requiredForCompliance: false,
    helperText: "Use this for documents that do not clearly fit the main binder categories yet.",
    documentTypes: [
      "Unknown document type",
      "Other certificate",
      "Other training document",
      "Other screening document",
      "Other supporting record",
    ],
  },
];

export const CAREGIVER_JOB_SEARCH_BINDER_SYSTEM_FOLDERS = CAREGIVER_JOB_BINDER_SUPPORT_GROUPS.map((group) => ({
  code: group.code,
  name: group.name,
  requiredForCompliance: group.requiredForCompliance,
}));

export const CAREGIVER_JOB_BINDER_DOCUMENT_TYPE_LIBRARY: Record<string, string[]> = CAREGIVER_JOB_BINDER_SUPPORT_GROUPS.reduce(
  (result: Record<string, string[]>, group) => {
    result[group.code] = group.documentTypes;
    return result;
  },
  {},
);
